import { createContext, useContext, ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ChurchProfile {
  id: string;
  church_name: string;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip_code: string | null;
  phone: string | null;
  email: string | null;
  bible_verse: string | null;
  bible_reference: string | null;
  abbreviation: string | null;
  created_by: string | null;
}

interface ChurchContextType {
  profile: ChurchProfile | null;
  loading: boolean;
  isOwner: boolean;
  updateProfile: (data: Partial<ChurchProfile>) => Promise<void>;
  uploadLogo: (file: File) => Promise<string>;
}

const ChurchContext = createContext<ChurchContextType | undefined>(undefined);

export function ChurchProvider({ children }: { children: ReactNode }) {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const userId = session?.user?.id;

  const { data: profile = null, isLoading: loading } = useQuery({
    queryKey: ["church_profile", userId],
    queryFn: async () => {
      if (!userId) return null;

      const { data: userRole, error: roleError } = await supabase
        .from("user_roles")
        .select("church_id")
        .eq("user_id", userId)
        .maybeSingle();
      if (roleError) throw roleError;

      const mappedChurchId = (userRole as { church_id?: string | null } | null)?.church_id;

      if (!mappedChurchId) return null;

      const { data, error } = await supabase
        .from("church_profile")
        .select("*")
        .eq("id", mappedChurchId)
        .single();
      if (error) throw error;
      return data as ChurchProfile;
    },
    enabled: !!userId,
  });

  const updateProfile = async (updates: Partial<ChurchProfile>) => {
    if (!profile) return;
    const { error } = await supabase
      .from("church_profile")
      .update(updates)
      .eq("id", profile.id);
    if (error) throw error;
    queryClient.invalidateQueries({ queryKey: ["church_profile", userId] });
  };

  const uploadLogo = async (file: File): Promise<string> => {
    if (!profile?.id) throw new Error("Church profile is not available");
    const ext = file.name.split(".").pop();
    const path = `church/${profile.id}/logos/church-logo-${Date.now()}.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from("church-assets")
      .upload(path, file, { upsert: true });
    if (uploadError) throw uploadError;

    const { data } = supabase.storage
      .from("church-assets")
      .getPublicUrl(path);

    return data.publicUrl;
  };

  const isOwner = !!profile?.created_by && profile.created_by === userId;

  return (
    <ChurchContext.Provider value={{ profile, loading, isOwner, updateProfile, uploadLogo }}>
      {children}
    </ChurchContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useChurch() {
  const context = useContext(ChurchContext);
  if (!context) throw new Error("useChurch must be used within ChurchProvider");
  return context;
}
